import { useEffect, useMemo, useState } from 'react';
import { LETTER_RETURN, RETURN_WORDS, ReturnKind, ReturnsRoom, returnsRoom } from '../../data/returns';
import { keyName, parseKey } from '../../music/key';
import type { PlayNote } from '../../audio/player';
import { usePlayer, usePrefs, useWidth } from '../hooks';
import { PlayIcon } from '../components/Icons';
import { PlayButton } from '../components/PlayButton';
import { Crumbs } from '../components/Shell';

const KINDS: ReturnKind[] = ['same', 'moved', 'decorated', 'changed', 'missing'];

/** Neutral for "as before", validated series colours for the rest, hollow for "left out". */
const FILL: Record<ReturnKind, string> = {
  same: 'var(--cell-same)',
  moved: 'var(--series-3)',
  decorated: 'var(--series-1)',
  changed: 'var(--series-4)',
  missing: 'none',
};

export function ReturnSwatch({ kind }: { kind: ReturnKind }) {
  return (
    <svg width="14" height="14" aria-hidden="true">
      <rect x="1" y="1" width="12" height="12" rx="3" fill={FILL[kind]} stroke={kind === 'missing' ? 'var(--ink-2)' : 'none'} strokeWidth={1.2} className={kind === 'missing' ? 'swatch-missing' : undefined} />
    </svg>
  );
}

/** A compact picture of a room for the index: one thin row per return. */
export function ReturnsMini({ rows, width = 200 }: { rows: Array<{ strip: string }>; width?: number }) {
  const n = Math.max(1, ...rows.map((r) => r.strip.length));
  const w = width / n;
  const h = Math.max(3, Math.min(8, 40 / Math.max(1, rows.length)));
  return (
    <svg width="100%" height={rows.length * (h + 1)} viewBox={`0 0 ${width} ${rows.length * (h + 1)}`} preserveAspectRatio="none" aria-hidden="true" style={{ display: 'block' }}>
      {rows.map((r, ri) =>
        [...r.strip].map((ch, i) => {
          const k = LETTER_RETURN[ch];
          return (
            <rect key={`${ri}-${i}`} x={i * w + 0.3} y={ri * (h + 1)} width={Math.max(0.4, w - 0.6)} height={h} rx={1} fill={FILL[k]} stroke={k === 'missing' ? 'var(--rule-strong)' : 'none'} strokeWidth={0.6} />
          );
        }),
      )}
    </svg>
  );
}

export function ReturnsRoomView({ id }: { id: string }) {
  const room = useMemo(() => returnsRoom(id), [id]);
  if (!room) {
    return (
      <div className="page-head">
        <h1>No such comparison</h1>
        <p>
          <a href="#forms">See all movements</a>
        </p>
      </div>
    );
  }
  return <Room key={room.id} room={room} />;
}

interface Segment {
  label: string;
  offset: number;
}

function Room({ room }: { room: ReturnsRoom }) {
  const { prefs } = usePrefs();
  const { depth } = prefs;
  const { player, position, playing } = usePlayer();
  const isSet = room.piece.kind === 'variations';
  // The speed at a point of the piece, relative to its tempo (an Adagio variation plays slower here too).
  const factorAt = (t: number) => {
    let f = 1;
    for (const x of room.piece.tempoMap ?? []) if (x.at <= t + 1e-6) f = x.factor;
    return f;
  };
  const [sel, setSel] = useState<{ row: number; col: number }>({ row: 0, col: 0 });
  const [segments, setSegments] = useState<Segment[]>([]);
  const [ref, width] = useWidth<HTMLDivElement>();
  useEffect(() => player.setTempo(prefs.tempo * (room.piece.tempo / 66)), [player, prefs.tempo, room.piece.tempo]);

  const measures = room.piece.measures;
  const span = (bar: number, before = 1) => {
    const i = measures.findIndex((m) => m.bar === bar);
    const a = measures[Math.max(0, i - before)];
    const b = measures[i];
    return { start: a.start, end: b.start + b.length };
  };
  const play = (items: Array<{ label: string; start: number; end: number }>) => {
    let t = 0;
    const notes: PlayNote[] = [];
    const segs: Segment[] = [];
    const tempoMap: Array<{ at: number; factor: number }> = [];
    for (const it of items) {
      // The excerpt keeps the piece's own speed changes (a bar before a slow variation stays fast).
      tempoMap.push({ at: t, factor: factorAt(it.start) });
      for (const x of room.piece.tempoMap ?? []) if (x.at > it.start + 1e-6 && x.at < it.end - 1e-6) tempoMap.push({ at: t + x.at - it.start, factor: x.factor });
      for (const n of room.piece.notes) {
        if (n.onset >= it.end - 1e-6 || n.onset + n.dur <= it.start + 1e-6) continue;
        const a = Math.max(n.onset, it.start);
        const b = Math.min(n.onset + n.dur, it.end);
        notes.push({ onset: t + a - it.start, dur: b - a, midi: n.midi, line: n.line, velocity: n.line === 0 ? 0.62 : 0.72 });
      }
      segs.push({ label: it.label, offset: t });
      t += it.end - it.start + 1.2;
    }
    setSegments(segs);
    player.setTempoMap(tempoMap);
    player.setNotes(notes, Math.max(0, t - 1.2));
    void player.play(0);
  };
  const refBar = (col: number) => room.reference.bars[col].bar;
  const playCell = (row: number, col: number) => {
    const items = [{ label: `${room.reference.label}, bar ${refBar(col)}`, ...span(refBar(col)) }];
    if (row === 0) {
      for (const r of room.rows) if (r.cells[col].bar !== null) items.push({ label: `${r.label}, bar ${r.cells[col].bar}`, ...span(r.cells[col].bar!) });
    } else {
      const c = room.rows[row - 1].cells[col];
      if (c.bar !== null) items.push({ label: `${room.rows[row - 1].label}, bar ${c.bar}`, ...span(c.bar) });
    }
    play(items);
  };
  const playSection = (row: number) => {
    const sec = row === 0 ? room.reference.section : room.rows[row - 1].section;
    play([{ label: row === 0 ? room.reference.label : room.rows[row - 1].label, start: sec.start, end: sec.end }]);
  };
  const pick = (row: number, col: number) => {
    setSel({ row, col });
    playCell(row, col);
  };

  const current = playing ? [...segments].reverse().find((s) => position >= s.offset - 1e-6) : undefined;
  const cols = room.reference.bars.length;
  const W = width || 700;
  const cw = W / cols;
  const rowH = 20;
  const allRows = [{ label: room.reference.label, key: null, cells: null as null | ReturnsRoom['rows'][number]['cells'] }, ...room.rows.map((r) => ({ label: r.label, key: r.key, cells: r.cells }))];
  const totals = room.rows.reduce(
    (a, r) => {
      KINDS.forEach((k) => (a[k] += r.counts[k]));
      return a;
    },
    { same: 0, moved: 0, decorated: 0, changed: 0, missing: 0 } as Record<ReturnKind, number>,
  );
  const cellsTotal = KINDS.reduce((a, k) => a + totals[k], 0) || 1;
  const selectedCell = sel.row > 0 ? room.rows[sel.row - 1].cells[sel.col] : null;
  const pct = (n: number) => Math.round((n / cellsTotal) * 100);
  const refrainParts = [
    `${pct(totals.same + totals.moved)}% of its bars return note for note`,
    totals.decorated ? `${pct(totals.decorated)}% keep the harmony under a new surface` : '',
    totals.changed ? `${pct(totals.changed)}% are rewritten` : '',
    totals.missing ? `${pct(totals.missing)}% are left out when Mozart shortens it` : '',
  ].filter(Boolean);
  const intro =
    room.kind === 'variations'
      ? `A theme and ${room.rows.length} variations. In ${pct(totals.decorated + totals.same + totals.moved)}% of the bars the theme’s harmony comes back under a new surface or unchanged: the variations change the clothes, not the harmony.`
      : `The refrain comes back ${['', 'once', 'twice'][room.rows.length] ?? `${room.rows.length} times`}. ${
          refrainParts.length > 1 ? `${refrainParts.slice(0, -1).join(', ')} and ${refrainParts[refrainParts.length - 1]}` : refrainParts[0]
        }.`;

  return (
    <div className="form-room">
      <Crumbs
        items={[
          { href: '#compare', label: 'Compare' },
          isSet ? { href: '#variations', label: 'Theme and variations' } : { href: '#forms', label: 'When the music returns' },
          { label: isSet ? `${room.piece.composer.split(' ').pop()}, ${room.piece.catalogue}` : room.piece.catalogue },
        ]}
      />
      <header className="page-head">
        <span className="label">{room.kind === 'variations' ? 'A theme and its variations' : 'Every return of the refrain'}</span>
        <h1>{room.piece.title}</h1>
        <p>
          {isSet ? `${room.piece.composer}, ${room.piece.catalogue}, ${keyName(parseKey(room.piece.homeKey))}. ${room.piece.tune ?? ''}` : `${room.piece.catalogue}, ${room.piece.subtitle}.`} {intro}
        </p>
        <p className="hint">
          Pick a cell to hear that bar of the {room.kind === 'variations' ? 'theme' : 'first statement'} and then the same bar in that {room.kind === 'variations' ? 'variation' : 'return'}; pick a bar of the top row to hear it
          every time it comes back.
        </p>
      </header>

      <div className="transport" style={{ marginBottom: 12 }}>
        <PlayButton
          playing={playing}
          label={
            sel.row === 0
              ? `Hear bar ${refBar(sel.col)} in every ${room.kind === 'variations' ? 'variation' : 'return'}`
              : `Hear bar ${refBar(sel.col)}: ${room.reference.label.toLowerCase()}, then ${room.rows[sel.row - 1].label.toLowerCase()}`
          }
          onPause={() => player.pause()}
          onPlay={() => playCell(sel.row, sel.col)}
        />
        <span className="where">{current ? `Now: ${current.label}` : ''}</span>
        <span className="grow" />
        <a className="btn quiet" href={`#${room.id}`}>
          {isSet ? 'Open the whole set' : 'Open the movement'}
        </a>
      </div>

      <div className="panel" style={{ padding: '14px 16px' }}>
        <div className="form-legend" role="list" aria-label="Legend">
          {KINDS.filter((k) => totals[k] > 0).map((k) => (
            <span key={k} role="listitem">
              <ReturnSwatch kind={k} /> {depth >= 2 ? RETURN_WORDS[k].name : RETURN_WORDS[k].word} <span className="muted">{totals[k]}</span>
            </span>
          ))}
        </div>
        <div className="returns-grid" style={{ marginTop: 12, ['--cols' as string]: cols }}>
          {allRows.map((r, ri) => (
            <div key={ri} className="returns-row">
              <button
                className="btn quiet returns-label"
                onClick={() => {
                  setSel((s) => ({ row: ri, col: s.col }));
                  playSection(ri);
                }}
                aria-label={`Play ${r.label} and choose it`}
                aria-pressed={sel.row === ri}
                title={`Play ${r.label}`}
              >
                <PlayIcon /> <span>{r.label}</span>
                {r.key && depth >= 2 ? <span className="muted"> · {keyName(r.key)}</span> : null}
              </button>
              <div ref={ri === 0 ? ref : undefined} className="returns-cells">
                <svg width="100%" height={rowH} viewBox={`0 0 ${W} ${rowH}`} preserveAspectRatio="none" role="group" aria-label={`${r.label}, bar by bar`}>
                  {Array.from({ length: cols }, (_, c) => {
                    const cell = r.cells ? r.cells[c] : null;
                    const k: ReturnKind | 'ref' = cell ? cell.kind : 'ref';
                    const selected = sel.row === ri && sel.col === c;
                    return (
                      <g key={c} onClick={() => pick(ri, c)} style={{ cursor: 'pointer' }}>
                        <title>
                          {cell
                            ? cell.bar !== null
                              ? `${r.label}, bar ${cell.bar}: ${RETURN_WORDS[cell.kind].word}`
                              : `${r.label}: bar ${refBar(c)} of the first statement is left out`
                            : `${r.label}, bar ${refBar(c)}: hear it every time`}
                        </title>
                        <rect x={c * cw} y={0} width={cw} height={rowH} fill="transparent" />
                        <rect
                          x={c * cw + 0.5}
                          y={2}
                          width={Math.max(0.5, cw - 1)}
                          height={rowH - 4}
                          rx={Math.min(3, cw / 3)}
                          fill={k === 'ref' ? 'var(--ink-2)' : FILL[k]}
                          opacity={k === 'ref' ? 0.35 : 1}
                          stroke={k === 'missing' ? 'var(--ink-2)' : 'none'}
                          strokeWidth={1}
                          strokeDasharray={k === 'missing' ? '3 2' : undefined}
                        />
                        {selected && <rect x={c * cw} y={0.5} width={cw} height={rowH - 1} fill="none" stroke="var(--ink)" strokeWidth={1.5} rx={2} />}
                      </g>
                    );
                  })}
                </svg>
              </div>
            </div>
          ))}
        </div>
      </div>

      <section className="panel" style={{ marginTop: 14 }} aria-live="polite">
        <div className="panel-head">
          <span className="label">
            {sel.row === 0 ? room.reference.label : room.rows[sel.row - 1].label} · bar {refBar(sel.col)} of the first statement
            {selectedCell?.bar != null ? ` → bar ${selectedCell.bar}` : ''}
          </span>
          <span style={{ display: 'flex', gap: 6 }}>
            <button className="btn" onClick={() => setSel((s) => ({ ...s, col: Math.max(0, s.col - 1) }))} aria-label="Previous bar">
              ←
            </button>
            <button className="btn" onClick={() => setSel((s) => ({ ...s, col: Math.min(cols - 1, s.col + 1) }))} aria-label="Next bar">
              →
            </button>
          </span>
        </div>
        {selectedCell ? (
          <>
            <h2 style={{ fontSize: '1.5rem', margin: '4px 0 6px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <ReturnSwatch kind={selectedCell.kind} /> {RETURN_WORDS[selectedCell.kind].word}
            </h2>
            <p style={{ margin: 0 }}>
              {selectedCell.bar === null
                ? 'This bar of the first statement does not come back here.'
                : `${Math.round(selectedCell.notes * 100)}% of the top and bass notes and ${Math.round(selectedCell.harmony * 100)}% of the harmony, beat by beat, match the first statement${selectedCell.shift ? ' after transposition' : ''}.`}
            </p>
          </>
        ) : (
          <p style={{ margin: 0 }}>The first statement. Play a bar to hear it followed by every return of the same bar.</p>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <button className="btn" onClick={() => playCell(sel.row, sel.col)}>
            <PlayIcon /> {sel.row === 0 ? 'Hear it every time' : 'Hear both'}
          </button>
        </div>
        {sel.row > 0 && room.rows[sel.row - 1].added.length > 0 && (
          <p className="muted" style={{ fontSize: '0.84rem', marginTop: 10 }}>
            {room.rows[sel.row - 1].label} also has {room.rows[sel.row - 1].added.length} bar{room.rows[sel.row - 1].added.length === 1 ? '' : 's'} of its own (bars{' '}
            {room.rows[sel.row - 1].added.slice(0, 6).join(', ')}
            {room.rows[sel.row - 1].added.length > 6 ? '…' : ''}).
          </p>
        )}
        <p className="muted" style={{ fontSize: '0.84rem', marginTop: 10 }}>
          Each excerpt starts one bar early. Bars are compared by their top and bass notes and by their harmony beat by beat; a bar can keep its harmony and change every
          note, which is what a variation does.
        </p>
      </section>
    </div>
  );
}
